import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {ReactChild, useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {RunWithRunsetInfo} from 'src/containers/RunsDataLoader';

import {
  RunsOutputArtifactsQueryResult,
  useRunsOutputArtifactsQuery,
} from '../../generated/graphql';
import {runColor} from '../../util/colors';
import DOC_URLS from '../../util/doc_urls';
import type {PanelProps} from '../../util/panelProps';
import {getCustomRunName} from '../../util/runs';
import {Key} from '../../util/runTypes';
import CodeDiff from '../CodeDiff';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {InstrumentedLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {CodeComparerConfig} from './common';

type PanelCodeComparerProps = PanelProps<CodeComparerConfig>;

function useRunsWithCodeArtifacts(
  runsOuputArtifactsQuery: RunsOutputArtifactsQueryResult,
  queriedRuns: RunWithRunsetInfo[]
) {
  return useMemo(() => {
    if (!runsOuputArtifactsQuery.data?.project?.runs?.edges) {
      return [];
    }

    return runsOuputArtifactsQuery.data.project.runs.edges.reduce<
      RunWithRunsetInfo[]
    >((acc, artifactRun) => {
      const codeArtifact = artifactRun.node.outputArtifacts?.edges?.find(
        a => a.node?.artifactType.name === 'code'
      );

      if (!codeArtifact) {
        return acc;
      }

      const run = queriedRuns.find(
        propRun => propRun.name === artifactRun.node.name
      );

      if (!run) {
        return acc;
      }

      const hasMatchingCodePath =
        codeArtifact.node?.artifactSequenceMembership?.files?.edges?.some(
          fNode =>
            fNode.node?.name != null &&
            fNode.node?.name === run.runInfo?.codePath
        );

      if (hasMatchingCodePath) {
        acc.push(run);
      }

      return acc;
    }, []);
  }, [runsOuputArtifactsQuery.data, queriedRuns]);
}

const PanelCodeComparer: React.FC<PanelCodeComparerProps> = props => {
  const entityName =
    props.query?.queries?.[0]?.entityName ?? props.data.entityName;
  const projectName =
    props.query?.queries?.[0]?.projectName ?? props.data.projectName;
  const [error, setError] = useState<ReactChild | undefined>(undefined);
  const [errorLogMessage, setErrorLogMessage] = useState<string | undefined>(
    undefined
  );
  const [runA, setRunA] = useState<RunWithRunsetInfo | undefined>(undefined);
  const [runB, setRunB] = useState<RunWithRunsetInfo | undefined>(undefined);
  const lastRuns = useRef(new Set<string>());

  const runsWithFileCode = useMemo(() => {
    return props.data.filtered.filter(
      r => r._wandb && (r._wandb.code_path || r._wandb.session_history)
    );
  }, [props.data.filtered]);

  // construct set so we can quickly check if a run is already in the list
  const runsWithFileCodeNames = useMemo(
    () => new Set(runsWithFileCode.map(r => r.name)),
    [runsWithFileCode]
  );

  const remainingRuns = props.data.filtered.filter(
    r => !runsWithFileCodeNames.has(r.name)
  );

  const codePaths = remainingRuns
    .map(r => r.runInfo?.codePath)
    .filter(r => r != null) as string[];
  // TODO: this should filter on the artifact type
  // but it seems we do not support filtering on artifact type yet
  const runsOuputArtifactsQuery = useRunsOutputArtifactsQuery({
    variables: {
      projectName,
      entityName,
      runNames: props.data.filtered.map(r => r.name),
      filenames: codePaths,
      // filters: JSON.stringify(toMongo({
      //   op: '=',
      //   key: {
      //     section: 'artifact',
      //     name: 'artifact_type',
      //   },
      //   value: 'code',
      // })),
    },
    skip: codePaths.length === 0,
  });

  const runsWithCodeArtifacts = useRunsWithCodeArtifacts(
    runsOuputArtifactsQuery,
    remainingRuns
  );

  const runsWithCode = useMemo(
    () =>
      [...runsWithFileCode, ...runsWithCodeArtifacts].map(run => {
        const customRunName = getCustomRunName(
          run.name,
          false,
          props.customRunNames
        );
        return {...run, customRunName};
      }),
    [runsWithFileCode, runsWithCodeArtifacts, props.customRunNames]
  );
  const isGrouped =
    props.pageQuery.runSets != null &&
    props.pageQuery.runSets.some(
      runSet => runSet.grouping != null && runSet.grouping?.length > 0
    );

  useEffect(() => {
    if (!props.loading) {
      if (props.data.filtered.length === 0) {
        setError(
          'Select at least one run to load data into this run comparer.'
        );
        setErrorLogMessage(
          'Select at least one run to load data into this run comparer.'
        );
      }
      const newRun = runsWithCode.find(r => !lastRuns.current.has(r.name));
      if (runA === undefined || runB === undefined) {
        if (runsWithCode.length === 1) {
          setError(undefined);
          setErrorLogMessage(undefined);
          setRunA(runsWithCode[0]);
          setRunB(runsWithCode[0]);
        } else if (runsWithCode.length === 0) {
          const errMsg = (
            <div style={{maxWidth: 300}}>
              {isGrouped ? (
                <>
                  To enable the Code Comparer panel,
                  <br />
                  turn off grouping.
                </>
              ) : (
                <>
                  No selected runs have saved code.
                  <br />
                  Enable code saving on your{' '}
                  <Link to="/settings">settings page</Link>, and learn more in{' '}
                  <TargetBlank href={DOC_URLS.codeComparer}>
                    the docs
                  </TargetBlank>
                  .
                </>
              )}
            </div>
          );
          setError(errMsg);
          setErrorLogMessage(
            isGrouped
              ? 'To enable the Code Comparer panel, turn off grouping.'
              : 'No selected runs have saved code. Enable code saving on your settings page, and learn more in the docs.'
          );
        } else if (runsWithCode.length > 1) {
          setError(undefined);
          setErrorLogMessage(undefined);
          setRunA(runsWithCode[0]);
          setRunB(runsWithCode[1]);
        }
      } else {
        if (newRun) {
          setError(undefined);
          setErrorLogMessage(undefined);
          setRunB(newRun);
        } else if (
          props.data.filtered.find(r => r.name === runB.name) == null
        ) {
          setError(undefined);
          setErrorLogMessage(undefined);
          setRunB(runsWithCode[runsWithCode.length > 1 ? 1 : 0]);
        }
        if (props.data.filtered.find(r => r.name === runA.name) == null) {
          setError(undefined);
          setErrorLogMessage(undefined);
          setRunA(runsWithCode[runsWithCode.length > 1 ? 1 : 0]);
        }
        if (newRun && runA.name === runB.name && runsWithCode.length > 1) {
          setRunA(runsWithCode[0]);
          setRunB(runsWithCode[1]);
        }
      }
    }
    lastRuns.current = new Set(runsWithCode.map(r => r.name));
  }, [runsWithCode, props, runA, runB, isGrouped]);

  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const stablePanelId = useStablePanelId();

  if (props.loading) {
    return <Loader name="panel-code-comparer" />;
  }

  const idToGroupingMap: {[key: string]: Key[] | undefined} = {};
  if (props.pageQuery.runSets) {
    props.pageQuery.runSets.forEach(runSet => {
      idToGroupingMap[runSet.id] = runSet.grouping;
    });
  }

  return (
    <div className="code-comparer-wrapper">
      <div className="code-comparer">
        <div className="header-slave">
          <div className="filler-cell" />
          {[runA, runB].map((run, i) => {
            if (run) {
              const groupKeys = idToGroupingMap[run.runsetInfo.id];
              const color = groupKeys
                ? runColor({
                    run,
                    groupKeys,
                    customRunColors: props.customRunColors,
                    colorPaletteOption,
                  })
                : undefined;
              return (
                <div
                  className="cell name-cell"
                  key={'code-dropdown-' + run.name + `-${i}`}
                  title={run.displayName}
                  style={{color, position: 'relative'}}>
                  <ModifiedDropdown
                    style={{color, display: 'flex', alignItems: 'center'}}
                    data-test="code-key-selector"
                    placeholder="Select Run"
                    options={runsWithCode.map(r => ({
                      key: r.id,
                      value: r.name,
                      text: r.customRunName ?? r.displayName,
                    }))}
                    value={i === 0 ? runA?.name : runB?.name}
                    search={true}
                    onChange={(e, {value}) => {
                      const selectedRun = runsWithCode.find(
                        r => r.name === (value as string)
                      );
                      setError(undefined);
                      if (i === 0) {
                        setRunA(selectedRun);
                      } else {
                        setRunB(selectedRun);
                      }
                    }}
                  />
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>
        {runA != null && (
          <div className="diff-toggle-wrapper">
            <LegacyWBIcon
              name={
                props.config.diff === 'split' ? 'unified-diff' : 'split-diff'
              }
              onClick={() =>
                props.updateConfig({
                  diff: props.config.diff === 'split' ? 'unified' : 'split',
                })
              }
            />
          </div>
        )}
        {error ? (
          <PanelError
            panelID={stablePanelId}
            panelViewType="Code Comparer"
            message={error}
            logErrorMessage={
              errorLogMessage || 'Code comparison error occurred'
            }
          />
        ) : (
          <div className="master-wrapper">
            {runA && runB && (
              <CodeDiff
                entityName={entityName}
                projectName={projectName}
                runA={runA}
                runB={runB}
                setError={(err: string | undefined) => {
                  setError(err);
                  setErrorLogMessage(err);
                }}
                split={props.config.diff === 'split'}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PanelCodeComparer;
